import "./App.css";
import React, { useState, useEffect } from "react";
import { useRoutes } from "react-router-dom";
import { ListNodePointsLayout } from "./pages/nodes";
import { RoutesLayout } from "./pages/routes";
import { AdminLayout } from "./pages/admin";
import { PDFRouteLayout } from "./pages/pdf-route/pdfPage";
import configService from "./services/configService";

/**
 * Loading component mostrado mientras se carga la configuración
 */
function LoadingScreen() {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      flexDirection: 'column',
      gap: '20px'
    }}>
      <div style={{
        border: '4px solid #f3f3f3',
        borderTop: '4px solid #3498db',
        borderRadius: '50%',
        width: '50px',
        height: '50px',
        animation: 'spin 1s linear infinite'
      }} />
      <p style={{ fontSize: '18px', color: '#666' }}>Cargando configuración...</p>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

/**
 * Error component mostrado si falla la carga de configuración
 */
function ErrorScreen({ error }: { error: string }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      flexDirection: 'column',
      gap: '20px',
      padding: '20px'
    }}>
      <h2 style={{ color: '#d32f2f' }}>Error al cargar la configuración</h2>
      <p style={{ color: '#666', maxWidth: '600px', textAlign: 'center' }}>{error}</p>
      <button
        onClick={() => window.location.reload()}
        style={{
          padding: '10px 20px',
          fontSize: '16px',
          backgroundColor: '#1976d2',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer'
        }}
      >
        Reintentar
      </button>
    </div>
  );
}

/**
 * AppContent - Componente que contiene las rutas de la aplicación
 */
function AppContent() {
  // Determinar el layout de la raíz basado en la configuración
  const isNodePointsEnabled = configService.isNodePointsEnabled();
  const rootElement = isNodePointsEnabled ? <ListNodePointsLayout /> : <RoutesLayout />;

  let routes = useRoutes([
    { path: "/", element: rootElement },
    { path: "rutas", element: <RoutesLayout /> },
    { path: "admin", element: <AdminLayout /> },
    { path: "/pdf-route", element: <PDFRouteLayout /> },
    // ...
  ]);
  return routes;
}

/**
 * App - Componente principal que maneja la inicialización de configuración
 */
export default function App() {
  const [configLoaded, setConfigLoaded] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);

  useEffect(() => {
    // Inicializar la configuración al montar el componente
    const initializeConfig = async () => {
      try {
        await configService.initialize();
        setConfigLoaded(true);
        console.log('App initialized with config:', configService.getConfig());
      } catch (error) {
        console.error('Failed to initialize config:', error);
        setConfigError(error instanceof Error ? error.message : 'Error desconocido');
      }
    };

    initializeConfig();
  }, []);

  // Mostrar pantalla de carga mientras se inicializa la configuración
  if (!configLoaded && !configError) {
    return <LoadingScreen />;
  }

  // Mostrar pantalla de error si falló la inicialización
  if (configError) {
    return <ErrorScreen error={configError} />;
  }

  // Renderizar la aplicación normal una vez cargada la configuración
  return <AppContent />;
}
